// Account sync (docs/tune-library-plan.md §4, step C): your saved tunes and
// how you play each tune follow you to every device you sign in on.
//
// Local-first: the store (localStorage) stays the truth the app reads, so
// everything works offline and signed out exactly as before. This engine
// reconciles it with the account (migration 0008: `user_tunes`,
// `user_tune_settings`) — at sign-in, a few seconds after a change, and when
// the app comes back into view — using the pure planner in
// state/accountSyncPlan.ts. Every sync is a whole reconcile, cheap at this
// scale (two small list queries), which is also what makes it self-healing: a
// sync that fails halfway writes no memory, so the next one simply plans again
// and every write it repeats is idempotent. Your play history and loves follow
// after (migration 0018, data/personalSync.ts); the history changes on every
// play, so it goes up at most every couple of minutes, and when the page hides.

import { useSyncExternalStore } from 'react';
import { bootAuth, getAuthState, getSupabaseRaw, subscribeAuth, useAuth } from '@/auth/session';
import type { Score } from '@/music/score/types';
import { useStore } from '@/state/store';
import {
  personalMemoryOf,
  planSettingsSync,
  planTuneSync,
  recordHash,
  settingsKeysOf,
  settingsRecordOf,
  type AccountSyncState,
  type TuneSettingsMaps,
  type TuneSettingsRecord,
} from '@/state/accountSyncPlan';
import { syncPersonal } from './personalSync';

const TUNES = 'user_tunes';
const SETTINGS = 'user_tune_settings';
/** Ids per `in()` filter: they travel in the request URL. */
const CHUNK = 40;
/** How long a change settles before it goes up. */
const PUSH_DELAY_MS = 3000;
/** How long a returning tab waits before asking again. */
const RESYNC_MS = 60 * 1000;
/** How long play history gathers before it goes up: it changes every half
 * minute while the band plays. */
const HISTORY_PUSH_DELAY_MS = 2 * 60 * 1000;

// --- the Supabase client, structurally ----------------------------------------

type Row = Record<string, unknown>;
type Result<T> = { data: T | null; error: { message: string } | null };
type Done = PromiseLike<{ error: { message: string } | null }>;

/** A filterable select: awaitable as a list (supabase builders are thenables). */
export type SyncFilter = PromiseLike<Result<Row[]>> & {
  eq(column: string, value: string): SyncFilter;
  in(column: string, values: string[]): SyncFilter;
};

/** The slice of supabase-js this module uses (tests fake it). */
export interface AccountSyncClient {
  from(table: string): {
    select(columns: string): SyncFilter;
    upsert(rows: Row[], options: { onConflict: string }): Done;
    delete(): { in(column: string, values: string[]): Done };
  };
  rpc(fn: string, args: Row): PromiseLike<Result<unknown>>;
}

let testClient: AccountSyncClient | null = null;

function client(): AccountSyncClient | null {
  return testClient ?? (getSupabaseRaw() as AccountSyncClient | null);
}

function chunks<T>(items: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += CHUNK) out.push(items.slice(i, i + CHUNK));
  return out;
}

async function rows(filter: SyncFilter): Promise<Row[]> {
  const { data, error } = await filter;
  if (error) throw new Error(error.message);
  return data ?? [];
}

async function done(write: Done): Promise<void> {
  const { error } = await write;
  if (error) throw new Error(error.message);
}

// --- status, for the Account section --------------------------------------------

export interface AccountSyncStatus {
  phase: 'idle' | 'syncing' | 'synced' | 'error';
  /** Tunes saved on this device that are not in the account yet. */
  deviceOnly: number;
  error: string | null;
}

const IDLE: AccountSyncStatus = { phase: 'idle', deviceOnly: 0, error: null };
let status: AccountSyncStatus = IDLE;
const listeners = new Set<() => void>();

function publish(next: Partial<AccountSyncStatus>) {
  status = { ...status, ...next };
  for (const listener of listeners) listener();
}

export function getAccountSyncStatus(): AccountSyncStatus {
  return status;
}

export function useAccountSyncStatus(): AccountSyncStatus {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => status,
  );
}

/** Whether a tune you save now goes to your account. */
export function useSavesToAccount(): boolean {
  const auth = useAuth();
  const sync = useStore((s) => s.accountSync);
  return auth.status === 'active' && !!sync && sync.userId === auth.userId && sync.uploadTunes;
}

// --- the reconcile ---------------------------------------------------------------

function mapsOf(s: TuneSettingsMaps): TuneSettingsMaps {
  return {
    tuneBands: s.tuneBands,
    tuneFeels: s.tuneFeels,
    tuneArrangements: s.tuneArrangements,
    tunePlayback: s.tunePlayback,
  };
}

function localHashes(maps: TuneSettingsMaps): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of settingsKeysOf(maps)) out[key] = recordHash(settingsRecordOf(maps, key));
  return out;
}

/** When this device changed each tune's record, as the store subscriber saw it. */
const localChangedAt: Record<string, number> = {};

let running = false;
let again = false;
/** The engine's own writes to the store, which are not your changes. */
let applying = false;

/**
 * Reconcile this device with the account once. Quietly does nothing signed
 * out, unconfigured or without an activated account; a failure is reported in
 * the status and leaves the memory untouched, so the next sync plans afresh.
 */
export async function syncAccountNow(): Promise<void> {
  if (running) {
    again = true;
    return;
  }
  const auth = getAuthState();
  const c = client();
  if (auth.status !== 'active' || !auth.userId || !c) {
    publish(IDLE);
    return;
  }
  const uid = auth.userId;
  running = true;
  publish({ phase: 'syncing', error: null });
  try {
    const remoteTunes = await rows(c.from(TUNES).select('tune_id').eq('owner', uid));
    const remoteSettings = await rows(
      c.from(SETTINGS).select('tune_key, doc, updated_at').eq('owner', uid),
    );

    const start = useStore.getState();
    const previous = start.accountSync?.userId === uid ? start.accountSync : null;
    const memory: AccountSyncState = previous ?? {
      userId: uid,
      tunes: [],
      settings: {},
      uploadTunes: false,
      lastSyncedAt: null,
    };
    // Nothing saved on this device: nothing to ask about. A device no account
    // has ever signed in on is yours: what was saved on it before you signed
    // in moves into your account, as its playlists do. Only tunes left by
    // ANOTHER account wait for your say-so (Add to my account).
    let uploadTunes =
      memory.uploadTunes || start.savedCharts.length === 0 || start.accountSync === null;

    const tunePlan = planTuneSync({
      local: start.savedCharts.map((tune) => tune.id).filter((id): id is string => !!id),
      remote: remoteTunes.map((row) => String(row.tune_id)),
      synced: memory.tunes,
      deleted: start.deletedSavedTunes,
      uploadTunes,
    });
    // Nothing waiting on your say-so: this device and the account are one
    // from here on, so what you save next goes up without asking.
    if (tunePlan.deviceOnly.length === 0) uploadTunes = true;

    const maps = mapsOf(start);
    const local = localHashes(maps);
    const remote: Record<string, { hash: string; updatedAt: string; doc: unknown }> = {};
    for (const row of remoteSettings) {
      if (typeof row.tune_key !== 'string') continue;
      remote[row.tune_key] = {
        hash: recordHash(row.doc),
        updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
        doc: row.doc,
      };
    }
    const settingsPlan = planSettingsSync({
      local,
      remote,
      synced: memory.settings,
      localChangedAt,
      now: Date.now(),
    });

    // Up first: every write is idempotent, so a failure part-way loses nothing.
    const now = new Date().toISOString();
    const byId = new Map(start.savedCharts.map((tune) => [tune.id, tune]));
    for (const ids of chunks(tunePlan.push)) {
      await done(
        c.from(TUNES).upsert(
          ids.map((id) => {
            const tune = byId.get(id)!;
            return {
              tune_id: id,
              title: tune.title ?? '',
              composer: tune.composer ?? '',
              doc: tune as unknown as Row,
              updated_at: now,
            };
          }),
          { onConflict: 'owner,tune_id' },
        ),
      );
    }
    for (const ids of chunks(tunePlan.deleteRemote)) {
      await done(c.from(TUNES).delete().in('tune_id', ids));
    }
    for (const keys of chunks(settingsPlan.push)) {
      await done(
        c.from(SETTINGS).upsert(
          keys.map((key) => ({
            tune_key: key,
            doc: settingsRecordOf(maps, key) as unknown as Row,
            updated_at: now,
          })),
          { onConflict: 'owner,tune_key' },
        ),
      );
    }
    for (const keys of chunks(settingsPlan.pushDelete)) {
      await done(c.from(SETTINGS).delete().in('tune_key', keys));
    }

    // Then down.
    const arriving: Score[] = [];
    for (const ids of chunks(tunePlan.pull)) {
      for (const row of await rows(
        c.from(TUNES).select('tune_id, doc').eq('owner', uid).in('tune_id', ids),
      )) {
        if (row.doc && typeof row.doc === 'object') {
          arriving.push({ ...(row.doc as Score), id: String(row.tune_id) });
        }
      }
    }

    // You may have changed things while the requests were out. A record you
    // changed meanwhile is not overwritten: it stays out of this round's
    // memory, so the next sync sees your change and sends it.
    const current = useStore.getState();
    const nowLocal = localHashes(mapsOf(current));
    const settings: Record<string, TuneSettingsRecord | null> = {};
    const synced = { ...settingsPlan.synced };
    for (const key of [...settingsPlan.pull, ...settingsPlan.pullDelete]) {
      if ((nowLocal[key] ?? '') !== (local[key] ?? '')) {
        if (memory.settings[key]) synced[key] = memory.settings[key];
        else delete synced[key];
        continue;
      }
      settings[key] = settingsPlan.pull.includes(key)
        ? (remote[key].doc as TuneSettingsRecord)
        : null;
    }
    applying = true;
    try {
      current.applyAccountPull({
        addTunes: arriving,
        removeTunes: tunePlan.deleteLocal,
        settings,
      });
      const after = useStore.getState();
      after.setAccountSync({
        ...personalMemoryOf(previous),
        userId: uid,
        tunes: tunePlan.synced,
        settings: synced,
        uploadTunes,
        lastSyncedAt: now,
      });
      // Every tombstone has been accounted for: deleted from the account, or
      // never there.
      after.clearDeletedSavedTunes(start.deletedSavedTunes);
    } finally {
      applying = false;
    }
    for (const key of settingsPlan.push) delete localChangedAt[key];
    for (const key of settingsPlan.pushDelete) delete localChangedAt[key];

    // Your play history and loves.
    const personal = await syncPersonal(c, uid, {
      memory: personalMemoryOf(useStore.getState().accountSync),
      now: Date.now(),
      apply: asOwnWrite,
    });
    asOwnWrite(() => {
      const memory = useStore.getState().accountSync;
      if (memory?.userId === uid)
        useStore.getState().setAccountSync({ ...memory, ...personal.memory });
    });
    if (personal.retryAfter) schedule(personal.retryAfter);
    publish({ phase: 'synced', deviceOnly: tunePlan.deviceOnly.length, error: null });
  } catch (e) {
    publish({ phase: 'error', error: e instanceof Error ? e.message : 'Sync failed.' });
  } finally {
    running = false;
    if (again) {
      again = false;
      void syncAccountNow();
    }
  }
}

/** You said the tunes saved on this device may go to your account. */
export function uploadDeviceTunes(): Promise<void> {
  const s = useStore.getState();
  const uid = getAuthState().userId;
  if (!uid) return Promise.resolve();
  const previous = s.accountSync?.userId === uid ? s.accountSync : null;
  s.setAccountSync({
    userId: uid,
    tunes: previous?.tunes ?? [],
    settings: previous?.settings ?? {},
    uploadTunes: true,
    lastSyncedAt: previous?.lastSyncedAt ?? null,
  });
  return syncAccountNow();
}

/** The sync's own store writes: not changes you made, so they send nothing. */
function asOwnWrite(write: () => void): void {
  const was = applying;
  applying = true;
  try {
    write();
  } finally {
    applying = was;
  }
}

/**
 * Apply a store write that is not a change made on this device — another tab
 * adopting what a second tab already changed (and will sync itself). The
 * change detector does not count it, so two tabs do not both push one edit.
 */
export function asForeignWrite<T>(write: () => T): T {
  const was = applying;
  applying = true;
  try {
    return write();
  } finally {
    applying = was;
  }
}

// --- when to sync ------------------------------------------------------------------

let pushTimer: ReturnType<typeof setTimeout> | null = null;
let historyTimer: ReturnType<typeof setTimeout> | null = null;

function schedule(delay: number) {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void syncAccountNow();
  }, delay);
}

/** Play history gathers: the first change starts the clock and later ones
 * ride along, so a tune playing for an hour goes up every couple of minutes,
 * not every half minute. */
function scheduleHistory() {
  if (historyTimer) return;
  historyTimer = setTimeout(() => {
    historyTimer = null;
    void syncAccountNow();
  }, HISTORY_PUSH_DELAY_MS);
}

/** Send gathered history now: the page is going away. */
function flushHistory() {
  if (!historyTimer) return;
  clearTimeout(historyTimer);
  historyTimer = null;
  void syncAccountNow();
}

/** Note which tunes' records changed between two store states. */
function noteChanges(next: TuneSettingsMaps, prev: TuneSettingsMaps): boolean {
  let changed = false;
  const at = Date.now();
  for (const name of ['tuneBands', 'tuneFeels', 'tuneArrangements', 'tunePlayback'] as const) {
    const a = next[name] as Record<string, unknown>;
    const b = prev[name] as Record<string, unknown>;
    if (a === b) continue;
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
      if (a[key] !== b[key]) {
        localChangedAt[key] = at;
        changed = true;
      }
    }
  }
  return changed;
}

/**
 * Keep this device and the account in step: now, whenever an account becomes
 * active, a few seconds after a change you make, and when the app returns.
 * Returns the cleanup.
 */
export function startAccountSync(now: () => number = Date.now): () => void {
  let wasActive = false;
  let lastSync = 0;
  const onAuth = () => {
    const active = getAuthState().status === 'active';
    if (active && !wasActive) {
      lastSync = now();
      schedule(0);
    }
    if (!active && wasActive) publish(IDLE);
    wasActive = active;
  };
  const unsubscribeAuth = subscribeAuth(onAuth);
  // Already signed in (a remount): the subscription only hears changes.
  onAuth();
  const unsubscribeStore = useStore.subscribe((next, prev) => {
    if (applying) return;
    const settingsChanged = noteChanges(next, prev);
    const tunesChanged =
      next.savedCharts !== prev.savedCharts || next.deletedSavedTunes !== prev.deletedSavedTunes;
    const tasteChanged =
      next.tuneTaste !== prev.tuneTaste || next.tuneTasteRemoved !== prev.tuneTasteRemoved;
    if (getAuthState().status !== 'active') return;
    if (settingsChanged || tunesChanged || tasteChanged) schedule(PUSH_DELAY_MS);
    if (next.tuneHistory !== prev.tuneHistory) scheduleHistory();
  });
  const onVisible = () => {
    if (document.visibilityState === 'hidden') {
      flushHistory();
      return;
    }
    if (document.visibilityState !== 'visible' || now() - lastSync < RESYNC_MS) return;
    if (getAuthState().status !== 'active') return;
    lastSync = now();
    schedule(0);
  };
  document.addEventListener('visibilitychange', onVisible);
  if (!testClient) void bootAuth();
  return () => {
    unsubscribeAuth();
    unsubscribeStore();
    document.removeEventListener('visibilitychange', onVisible);
    if (pushTimer) clearTimeout(pushTimer);
    if (historyTimer) clearTimeout(historyTimer);
    historyTimer = null;
  };
}

// --- test seams ----------------------------------------------------------------------

export function __setAccountSyncTestClient(c: AccountSyncClient | null): void {
  testClient = c;
}

export function __resetAccountSyncForTests(): void {
  status = IDLE;
  running = false;
  again = false;
  for (const key of Object.keys(localChangedAt)) delete localChangedAt[key];
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = null;
  if (historyTimer) clearTimeout(historyTimer);
  historyTimer = null;
}
